import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    useForm,
    Controller,
} from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import Autocomplete from "@mui/material/Autocomplete";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import {
    Person as PersonIcon,
    Lock as LockIcon,
    Work as WorkIcon,
    Badge as BadgeIcon,
    AssignmentInd as AssignmentIndIcon,
    ToggleOn as ToggleOnIcon,
} from "@mui/icons-material";

import { useRoles } from "../../hooks/useRoles";
import { useServicesTypesProject } from "../../hooks/administration/useServicesTypesProject";
import axiosInstance from "../../api/axiosInstance";

// ============================================================
// CONSTANTES
// ============================================================

const ESTADOS = {
    ACTIVO: "A",
    INACTIVO: "I",
    ELIMINADO: "E",
};

const ESTADO_OPTIONS = [
    {
        value: ESTADOS.ACTIVO,
        label: "ACTIVO",
        color: "success",
    },
    {
        value: ESTADOS.INACTIVO,
        label: "INACTIVO",
        color: "warning",
    },
    {
        value: ESTADOS.ELIMINADO,
        label: "ELIMINADO",
        color: "error",
    },
];

// ============================================================
// HELPERS
// ============================================================

const getSerProId = (item) => {
    if (!item) {
        return null;
    }

    const value =
        item.serproid ??
        item.SerProID ??
        item.serProId ??
        item.SerProId;

    const id = Number(value);

    return Number.isInteger(id) && id > 0
        ? id
        : null;
};

const getProNom = (item) =>
    item?.pronom ??
    item?.ProNom ??
    "-";

const getSerProNom = (item) =>
    item?.serpronom ??
    item?.SerProNom ??
    "-";

const getTipSerNom = (item) =>
    item?.tipsernom ??
    item?.TipSerNom ??
    "-";

const getRolSerPro = (item) =>
    item?.rolserpro ??
    item?.RolSerPro ??
    item?.rolSerPro ??
    "";

const getPersonLabel = (person) => {
    if (!person) {
        return "";
    }

    const nombre = [
        person.pernom ?? person.PerNom ?? "",
        person.perape ?? person.PerApe ?? "",
    ]
        .filter(Boolean)
        .join(" ")
        .trim();

    const dni =
        person.perdni ??
        person.PerDNI;

    return dni
        ? `${nombre} (${dni})`
        : nombre;
};

const getPersonId = (person) =>
    Number(
        person?.perid ??
        person?.PerID
    );

const normalizeDefaultServices = (
    services = []
) => {
    if (!Array.isArray(services)) {
        return [];
    }

    const normalized = services
        .map((service) => {
            const serProId =
                getSerProId(service);

            if (!serProId) {
                return null;
            }

            return {
                serProId,
                rolSerPro:
                    getRolSerPro(service)
                        ?.trim() ||
                    null,
            };
        })
        .filter(Boolean);

    const uniqueServices = new Map();

    for (const service of normalized) {
        uniqueServices.set(
            service.serProId,
            service
        );
    }

    return Array.from(
        uniqueServices.values()
    );
};

const normalizeRoleName = (role) =>
    String(
        role?.rolnom ??
        role?.RolNom ??
        ""
    )
        .trim()
        .toUpperCase();

// ============================================================
// VALIDACIÓN
// ============================================================

const createValidationSchema = (
    isEditing
) =>
    yup.object({
        perid: yup
            .string()
            .required(
                "Debe seleccionar una persona"
            ),

        rolid: yup
            .string()
            .required(
                "Debe seleccionar un rol"
            ),

        nomusu: yup
            .string()
            .trim()
            .required(
                "El nombre de usuario es obligatorio"
            )
            .max(
                32,
                "El nombre de usuario no puede superar los 32 caracteres"
            ),

        pas: yup
            .string()
            .transform((value) =>
                value === ""
                    ? undefined
                    : value
            )
            .when([], {
                is: () => !isEditing,

                then: (schema) =>
                    schema
                        .required(
                            "La contraseña es obligatoria"
                        )
                        .min(
                            6,
                            "La contraseña debe tener al menos 6 caracteres"
                        ),

                otherwise: (schema) =>
                    schema
                        .notRequired()
                        .test(
                            "password-length",
                            "La contraseña debe tener al menos 8 caracteres",
                            (value) =>
                                !value ||
                                value.length >= 8
                        ),
            }),

        empcargo: yup
            .string()
            .trim()
            .required(
                "El cargo es obligatorio"
            )
            .max(
                100,
                "El cargo no puede superar los 100 caracteres"
            ),

        estreg: yup
            .string()
            .oneOf(
                Object.values(ESTADOS),
                "Estado inválido"
            )
            .required(
                "El estado es obligatorio"
            ),

        isinv: yup
            .boolean()
            .default(false),

        serviciosProyecto: yup
            .array()
            .of(
                yup.object({
                    serProId: yup
                        .number()
                        .integer()
                        .positive()
                        .required(),

                    rolSerPro: yup
                        .string()
                        .nullable(),
                })
            )
            .default([]),
    });

// ============================================================
// COMPONENTE
// ============================================================

const EmployeeForm = ({
    open,
    handleClose,
    onSubmit,
    defaultValues = {},
    isSubmitting = false,
}) => {
    const isEditing =
        Boolean(defaultValues?.empid);

    const [
        showPassword,
        setShowPassword,
    ] = useState(false);

    const [
        assignedServicesData,
        setAssignedServicesData,
    ] = useState([]);

    const [
        serviceSearch,
        setServiceSearch,
    ] = useState("");

    const [
        availablePersons,
        setAvailablePersons,
    ] = useState([]);

    const [
        searchText,
        setSearchText,
    ] = useState("");

    const [
        loadingPersons,
        setLoadingPersons,
    ] = useState(false);

    const searchTimeoutRef =
        useRef(null);

    // ========================================================
    // DATOS EXTERNOS
    // ========================================================

    const {
        roles = [],
        isLoading: loadingRoles,
    } = useRoles();

    const {
        servicesTypesProject = [],
        isLoading: loadingServices,
    } = useServicesTypesProject();

    // ========================================================
    // DATOS POR DEFECTO
    // ========================================================

    const defaultPerson = useMemo(() => {
        if (!defaultValues?.perid) {
            return null;
        }

        return {
            perid: Number(
                defaultValues.perid
            ),
            pernom:
                defaultValues.pernom ??
                defaultValues.PerNom ??
                "",
            perape:
                defaultValues.perape ??
                defaultValues.PerApe ??
                "",
            perdni:
                defaultValues.perdni ??
                defaultValues.PerDNI ??
                "",
        };
    }, [
        defaultValues?.perid,
        defaultValues?.pernom,
        defaultValues?.PerNom,
        defaultValues?.perape,
        defaultValues?.PerApe,
        defaultValues?.perdni,
        defaultValues?.PerDNI,
    ]);

    const defaultServices = useMemo(() => {
        const services =
            defaultValues
                ?.serviciosProyecto ??
            defaultValues
                ?.ServiciosProyecto ??
            [];

        return normalizeDefaultServices(
            services
        ).sort(
            (a, b) =>
                a.serProId -
                b.serProId
        );
    }, [
        defaultValues
            ?.serviciosProyecto,
        defaultValues
            ?.ServiciosProyecto,
    ]);

    // ========================================================
    // REACT HOOK FORM
    // ========================================================

    const validationSchema =
        useMemo(
            () =>
                createValidationSchema(
                    isEditing
                ),
            [isEditing]
        );

    const {
        control,
        register,
        handleSubmit,
        formState: {
            errors,
            isSubmitting:
            isFormSubmitting,
        },
        reset,
        watch,
        setValue,
    } = useForm({
        resolver:
            yupResolver(
                validationSchema
            ),

        defaultValues: {
            perid: "",
            rolid: "",
            nomusu: "",
            pas: "",
            empcargo: "",
            estreg:
                ESTADOS.ACTIVO,
            isinv: false,
            serviciosProyecto: [],
        },
    });

    const selectedRoleId =
        Number(watch("rolid"));

    const watchIsInv =
        Boolean(watch("isinv"));

    const selectedRole =
        useMemo(
            () =>
                roles.find(
                    (role) =>
                        Number(
                            role.rolid ??
                            role.RolID
                        ) ===
                        selectedRoleId
                ) ?? null,
            [
                roles,
                selectedRoleId,
            ]
        );

    const selectedRoleName =
        normalizeRoleName(
            selectedRole
        );

    // ========================================================
    // SERVICIOS
    // ========================================================

    const servicesOptions =
        useMemo(
            () =>
                Array.isArray(
                    servicesTypesProject
                )
                    ? servicesTypesProject
                    : [],
            [servicesTypesProject]
        );

    const assignedServiceIds =
        useMemo(
            () =>
                assignedServicesData.map(
                    (item) =>
                        item.serProId
                ),
            [assignedServicesData]
        );

    const assignedServices =
        useMemo(() => {
            const assignedSet =
                new Set(
                    assignedServiceIds
                );

            return servicesOptions.filter(
                (service) =>
                    assignedSet.has(
                        getSerProId(
                            service
                        )
                    )
            );
        }, [
            servicesOptions,
            assignedServiceIds,
        ]);

    const availableServices =
        useMemo(() => {
            const assignedSet =
                new Set(
                    assignedServiceIds
                );

            return servicesOptions.filter(
                (service) =>
                    !assignedSet.has(
                        getSerProId(
                            service
                        )
                    )
            );
        }, [
            servicesOptions,
            assignedServiceIds,
        ]);

    const filteredAvailableServices =
        useMemo(() => {
            const term =
                serviceSearch
                    .trim()
                    .toLowerCase();

            if (!term) {
                return availableServices;
            }

            return availableServices.filter(
                (service) => {
                    const searchFields = [
                        getSerProNom(
                            service
                        ),
                        getProNom(service),
                        getTipSerNom(
                            service
                        ),
                    ];

                    return searchFields.some(
                        (field) =>
                            String(field)
                                .toLowerCase()
                                .includes(
                                    term
                                )
                    );
                }
            );
        }, [
            availableServices,
            serviceSearch,
        ]);

    // ========================================================
    // PERSONAS
    // ========================================================

    const personOptions =
        useMemo(() => {
            if (
                !isEditing ||
                !defaultPerson
            ) {
                return availablePersons;
            }

            const exists =
                availablePersons.some(
                    (person) =>
                        getPersonId(
                            person
                        ) ===
                        getPersonId(
                            defaultPerson
                        )
                );

            return exists
                ? availablePersons
                : [
                    defaultPerson,
                    ...availablePersons,
                ];
        }, [
            isEditing,
            defaultPerson,
            availablePersons,
        ]);

    // ========================================================
    // EFECTOS
    // ========================================================

    useEffect(() => {
        if (!open) {
            return;
        }

        reset({
            perid:
                defaultValues?.perid
                    ? String(
                        defaultValues.perid
                    )
                    : "",

            rolid:
                defaultValues?.rolid
                    ? String(
                        defaultValues.rolid
                    )
                    : "",

            nomusu:
                defaultValues?.nomusu ??
                "",

            pas: "",

            empcargo:
                defaultValues
                    ?.empcargo ??
                "",

            estreg:
                defaultValues
                    ?.estreg ??
                ESTADOS.ACTIVO,

            isinv:
                Boolean(
                    defaultValues
                        ?.isinv
                ),

            serviciosProyecto:
                defaultServices,
        });

        setAssignedServicesData(
            defaultServices
        );

        setAvailablePersons([]);
        setSearchText("");
        setServiceSearch("");
        setShowPassword(false);
    }, [
        open,
        reset,
        defaultValues,
        defaultServices,
    ]);

    useEffect(() => {
        if (
            isEditing ||
            !open
        ) {
            return undefined;
        }

        const term =
            searchText.trim();

        if (term.length < 2) {
            setAvailablePersons([]);
            setLoadingPersons(false);

            return undefined;
        }

        if (
            searchTimeoutRef.current
        ) {
            clearTimeout(
                searchTimeoutRef.current
            );
        }

        const controller =
            new AbortController();

        searchTimeoutRef.current =
            setTimeout(
                async () => {
                    try {
                        setLoadingPersons(
                            true
                        );

                        const response =
                            await axiosInstance.get(
                                "/persons/basic/search",
                                {
                                    params: {
                                        search:
                                            term,
                                    },
                                    signal:
                                        controller.signal,
                                }
                            );

                        setAvailablePersons(
                            Array.isArray(
                                response.data
                            )
                                ? response.data
                                : []
                        );
                    } catch (error) {
                        const isCanceled =
                            error?.name ===
                            "CanceledError" ||
                            error?.code ===
                            "ERR_CANCELED";

                        if (!isCanceled) {
                            console.error(
                                "Error al buscar personas:",
                                error
                            );

                            setAvailablePersons(
                                []
                            );
                        }
                    } finally {
                        if (
                            !controller
                                .signal
                                .aborted
                        ) {
                            setLoadingPersons(
                                false
                            );
                        }
                    }
                },
                400
            );

        return () => {
            if (
                searchTimeoutRef.current
            ) {
                clearTimeout(
                    searchTimeoutRef.current
                );
            }

            controller.abort();
        };
    }, [
        searchText,
        isEditing,
        open,
    ]);

    useEffect(() => {
        setValue(
            "serviciosProyecto",
            assignedServicesData,
            {
                shouldValidate: true,
                shouldDirty: true,
            }
        );
    }, [
        assignedServicesData,
        setValue,
    ]);

    // ========================================================
    // HANDLERS
    // ========================================================

    const handleFormClose =
        useCallback(() => {
            if (
                isSubmitting ||
                isFormSubmitting
            ) {
                return;
            }

            reset();

            setAssignedServicesData(
                []
            );

            setAvailablePersons(
                []
            );

            setServiceSearch("");
            setSearchText("");
            setShowPassword(false);

            handleClose();
        }, [
            reset,
            handleClose,
            isSubmitting,
            isFormSubmitting,
        ]);

    const handleAddService =
        useCallback(
            (service) => {
                const serProId =
                    getSerProId(
                        service
                    );

                if (!serProId) {
                    return;
                }

                setAssignedServicesData(
                    (current) => {
                        const alreadyExists =
                            current.some(
                                (item) =>
                                    item.serProId ===
                                    serProId
                            );

                        if (
                            alreadyExists
                        ) {
                            return current;
                        }

                        return [
                            ...current,
                            {
                                serProId,

                                rolSerPro:
                                    selectedRoleName ||
                                    null,
                            },
                        ];
                    }
                );
            },
            [selectedRoleName]
        );

    const handleRemoveService =
        useCallback(
            (serProId) => {
                const id =
                    Number(serProId);

                if (
                    !Number.isInteger(
                        id
                    )
                ) {
                    return;
                }

                setAssignedServicesData(
                    (current) =>
                        current.filter(
                            (item) =>
                                item.serProId !==
                                id
                        )
                );
            },
            []
        );

    const handleServiceRoleChange =
        useCallback(
            (
                serProId,
                value
            ) => {
                const id =
                    Number(serProId);

                if (
                    !Number.isInteger(
                        id
                    )
                ) {
                    return;
                }

                setAssignedServicesData(
                    (current) =>
                        current.map(
                            (item) =>
                                item.serProId ===
                                    id
                                    ? {
                                        ...item,

                                        rolSerPro:
                                            value ||
                                            null,
                                    }
                                    : item
                        )
                );
            },
            []
        );

    const handleFormSubmit =
        useCallback(
            (data) => {
                const payload = {
                    perid:
                        Number(
                            data.perid
                        ),

                    rolid:
                        Number(
                            data.rolid
                        ),

                    nomusu:
                        data.nomusu
                            .trim(),

                    empcargo:
                        data.empcargo
                            ?.trim() ||
                        null,

                    estreg:
                        data.estreg,

                    isinv:
                        Boolean(
                            data.isinv
                        ),

                    serviciosProyecto:
                        assignedServicesData.map(
                            (
                                assignment
                            ) => ({
                                serProId:
                                    Number(
                                        assignment.serProId
                                    ),

                                rolSerPro:
                                    assignment
                                        .rolSerPro
                                        ?.trim() ||
                                    null,
                            })
                        ),
                };

                if (
                    data.pas &&
                    data.pas.trim()
                ) {
                    payload.pas =
                        data.pas;
                }

                onSubmit(payload);
            },
            [
                onSubmit,
                assignedServicesData,
            ]
        );

    const submitting =
        isSubmitting ||
        isFormSubmitting;

    // ========================================================
    // RENDER
    // ========================================================

    return (
        <Dialog
            open={open}
            onClose={handleFormClose}
            fullWidth
            maxWidth="md"
            disableEscapeKeyDown={
                submitting
            }
        >
            <DialogTitle
                sx={{
                    bgcolor:
                        "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems:
                        "center",
                    gap: 2,
                }}
            >
                <AssignmentIndIcon />

                {isEditing
                    ? "Editar empleado"
                    : "Crear nuevo empleado"}
            </DialogTitle>

            <form
                onSubmit={handleSubmit(
                    handleFormSubmit
                )}
                noValidate
            >
                <DialogContent dividers>
                    <Grid
                        container
                        spacing={3}
                    >
                        {/* ================================= */}
                        {/* INFORMACIÓN BÁSICA */}
                        {/* ================================= */}

                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    gap: 1,
                                }}
                            >
                                <PersonIcon color="primary" />

                                Información básica
                            </Typography>

                            <Controller
                                name="perid"
                                control={control}
                                render={({
                                    field,
                                }) => (
                                    <Autocomplete
                                        options={
                                            personOptions
                                        }
                                        loading={
                                            loadingPersons
                                        }
                                        disabled={
                                            isEditing ||
                                            submitting
                                        }
                                        value={
                                            personOptions.find(
                                                (
                                                    person
                                                ) =>
                                                    getPersonId(
                                                        person
                                                    ) ===
                                                    Number(
                                                        field.value
                                                    )
                                            ) ??
                                            null
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value
                                        ) =>
                                            getPersonId(
                                                option
                                            ) ===
                                            getPersonId(
                                                value
                                            )
                                        }
                                        getOptionLabel={
                                            getPersonLabel
                                        }
                                        onChange={(
                                            _,
                                            newValue
                                        ) => {
                                            field.onChange(
                                                newValue
                                                    ? String(
                                                        getPersonId(
                                                            newValue
                                                        )
                                                    )
                                                    : ""
                                            );
                                        }}
                                        onInputChange={(
                                            _,
                                            value,
                                            reason
                                        ) => {
                                            if (
                                                reason !==
                                                "reset" &&
                                                !isEditing
                                            ) {
                                                setSearchText(
                                                    value ??
                                                    ""
                                                );
                                            }
                                        }}
                                        noOptionsText={
                                            isEditing
                                                ? "La persona no puede modificarse"
                                                : searchText.trim()
                                                    .length <
                                                    2
                                                    ? "Escriba al menos 2 caracteres"
                                                    : "Sin resultados"
                                        }
                                        renderInput={(
                                            params
                                        ) => (
                                            <TextField
                                                {...params}
                                                label="Persona"
                                                margin="normal"
                                                error={
                                                    Boolean(
                                                        errors.perid
                                                    )
                                                }
                                                helperText={
                                                    errors
                                                        .perid
                                                        ?.message
                                                }
                                                InputProps={{
                                                    ...params.InputProps,

                                                    startAdornment:
                                                        (
                                                            <InputAdornment position="start">
                                                                <PersonIcon
                                                                    color={
                                                                        errors.perid
                                                                            ? "error"
                                                                            : "action"
                                                                    }
                                                                />
                                                            </InputAdornment>
                                                        ),

                                                    endAdornment:
                                                        (
                                                            <>
                                                                {loadingPersons && (
                                                                    <CircularProgress
                                                                        size={
                                                                            20
                                                                        }
                                                                    />
                                                                )}

                                                                {
                                                                    params
                                                                        .InputProps
                                                                        .endAdornment
                                                                }
                                                            </>
                                                        ),
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly:
                                                        isEditing,
                                                }}
                                            />
                                        )}
                                        renderOption={(
                                            props,
                                            option
                                        ) => (
                                            <li
                                                {...props}
                                                key={
                                                    getPersonId(
                                                        option
                                                    )
                                                }
                                                style={{
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: 10,
                                                }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        bgcolor:
                                                            "primary.main",
                                                    }}
                                                >
                                                    {String(
                                                        option.pernom ??
                                                        option.PerNom ??
                                                        ""
                                                    ).charAt(
                                                        0
                                                    )}

                                                    {String(
                                                        option.perape ??
                                                        option.PerApe ??
                                                        ""
                                                    ).charAt(
                                                        0
                                                    )}
                                                </Avatar>

                                                <Box>
                                                    <Typography variant="body2">
                                                        {option.pernom ??
                                                            option.PerNom}{" "}
                                                        {option.perape ??
                                                            option.PerApe}
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {option.perdni ??
                                                            option.PerDNI ??
                                                            "Sin DNI"}
                                                    </Typography>
                                                </Box>
                                            </li>
                                        )}
                                    />
                                )}
                            />

                            <TextField
                                label="Usuario"
                                fullWidth
                                margin="normal"
                                disabled={
                                    submitting
                                }
                                inputProps={{
                                    maxLength: 32,
                                }}
                                {...register(
                                    "nomusu"
                                )}
                                error={Boolean(
                                    errors.nomusu
                                )}
                                helperText={
                                    errors.nomusu
                                        ?.message
                                }
                                InputProps={{
                                    startAdornment:
                                        (
                                            <InputAdornment position="start">
                                                <BadgeIcon
                                                    color={
                                                        errors.nomusu
                                                            ? "error"
                                                            : "action"
                                                    }
                                                />
                                            </InputAdornment>
                                        ),
                                }}
                            />

                            <TextField
                                label={
                                    isEditing
                                        ? "Nueva contraseña (opcional)"
                                        : "Contraseña"
                                }
                                type={
                                    showPassword
                                        ? "text"
                                        : "password"
                                }
                                fullWidth
                                margin="normal"
                                disabled={
                                    submitting
                                }
                                {...register("pas")}
                                error={Boolean(
                                    errors.pas
                                )}
                                helperText={
                                    errors.pas
                                        ?.message ??
                                    (isEditing
                                        ? "Déjelo vacío para conservar la contraseña actual"
                                        : "Mínimo 8 caracteres")
                                }
                                InputProps={{
                                    startAdornment:
                                        (
                                            <InputAdornment position="start">
                                                <LockIcon
                                                    color={
                                                        errors.pas
                                                            ? "error"
                                                            : "action"
                                                    }
                                                />
                                            </InputAdornment>
                                        ),

                                    endAdornment:
                                        (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    type="button"
                                                    edge="end"
                                                    disabled={
                                                        submitting
                                                    }
                                                    onClick={() =>
                                                        setShowPassword(
                                                            (
                                                                current
                                                            ) =>
                                                                !current
                                                        )
                                                    }
                                                    aria-label={
                                                        showPassword
                                                            ? "Ocultar contraseña"
                                                            : "Mostrar contraseña"
                                                    }
                                                >
                                                    {showPassword
                                                        ? (
                                                            <VisibilityOff />
                                                        )
                                                        : (
                                                            <Visibility />
                                                        )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                }}
                            />
                        </Grid>

                        {/* ================================= */}
                        {/* INFORMACIÓN LABORAL */}
                        {/* ================================= */}

                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    gap: 1,
                                }}
                            >
                                <WorkIcon color="primary" />

                                Información laboral
                            </Typography>

                            <Controller
                                name="rolid"
                                control={control}
                                render={({
                                    field,
                                }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label="Rol"
                                        fullWidth
                                        margin="normal"
                                        disabled={
                                            loadingRoles ||
                                            submitting
                                        }
                                        error={Boolean(
                                            errors.rolid
                                        )}
                                        helperText={
                                            errors
                                                .rolid
                                                ?.message
                                        }
                                    >
                                        <MenuItem value="">
                                            Seleccione un rol
                                        </MenuItem>

                                        {roles.map(
                                            (role) => {
                                                const roleId =
                                                    role.rolid ??
                                                    role.RolID;

                                                const roleName =
                                                    role.rolnom ??
                                                    role.RolNom;

                                                return (
                                                    <MenuItem
                                                        key={
                                                            roleId
                                                        }
                                                        value={String(
                                                            roleId
                                                        )}
                                                    >
                                                        {
                                                            roleName
                                                        }
                                                    </MenuItem>
                                                );
                                            }
                                        )}
                                    </TextField>
                                )}
                            />

                            <TextField
                                label="Cargo"
                                fullWidth
                                margin="normal"
                                disabled={
                                    submitting
                                }
                                inputProps={{
                                    maxLength: 100,
                                }}
                                {...register(
                                    "empcargo"
                                )}
                                error={Boolean(
                                    errors.empcargo
                                )}
                                helperText={
                                    errors
                                        .empcargo
                                        ?.message
                                }
                            />

                            <Controller
                                name="estreg"
                                control={control}
                                render={({
                                    field,
                                }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label="Estado"
                                        fullWidth
                                        margin="normal"
                                        disabled={
                                            submitting
                                        }
                                        error={Boolean(
                                            errors.estreg
                                        )}
                                        helperText={
                                            errors
                                                .estreg
                                                ?.message
                                        }
                                        InputProps={{
                                            startAdornment:
                                                (
                                                    <InputAdornment position="start">
                                                        <ToggleOnIcon />
                                                    </InputAdornment>
                                                ),
                                        }}
                                    >
                                        {ESTADO_OPTIONS.map(
                                            (
                                                estado
                                            ) => (
                                                <MenuItem
                                                    key={
                                                        estado.value
                                                    }
                                                    value={
                                                        estado.value
                                                    }
                                                >
                                                    <Chip
                                                        label={
                                                            estado.label
                                                        }
                                                        color={
                                                            estado.color
                                                        }
                                                        size="small"
                                                    />
                                                </MenuItem>
                                            )
                                        )}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        {/* ================================= */}
                        {/* INVITADO */}
                        {/* ================================= */}

                        <Grid
                            item
                            xs={12}
                        >
                            <Controller
                                name="isinv"
                                control={control}
                                render={({
                                    field,
                                }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(
                                                    field.value
                                                )}
                                                disabled={
                                                    submitting
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    field.onChange(
                                                        event
                                                            .target
                                                            .checked
                                                    )
                                                }
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box
                                                component="span"
                                                sx={{
                                                    display:
                                                        "inline-flex",
                                                    alignItems:
                                                        "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography component="span">
                                                    ¿Es usuario invitado?
                                                </Typography>

                                                {watchIsInv && (
                                                    <Chip
                                                        label="Sí"
                                                        size="small"
                                                        color="info"
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                )}
                            />

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Esta propiedad es independiente de los servicios-proyecto asignados.
                            </Typography>
                        </Grid>

                        {/* ================================= */}
                        {/* SERVICIOS-PROYECTO */}
                        {/* ================================= */}

                        <Grid
                            item
                            xs={12}
                        >
                            <Box mt={1}>
                                <Typography variant="h6">
                                    Servicios-proyecto asignados
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                >
                                    Seleccione los servicios en los que el empleado puede trabajar.
                                </Typography>

                                {!selectedRoleId && (
                                    <Alert
                                        severity="info"
                                        sx={{
                                            mt: 2,
                                        }}
                                    >
                                        Seleccione primero el rol del empleado. Este se utilizará como función inicial dentro de cada servicio.
                                    </Alert>
                                )}

                                <Grid
                                    container
                                    spacing={2}
                                    mt={1}
                                >
                                    {/* ============================= */}
                                    {/* SERVICIOS DISPONIBLES */}
                                    {/* ============================= */}

                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: 2,
                                                height:
                                                    "100%",
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <AddCircleIcon color="primary" />

                                                Servicios disponibles
                                            </Typography>

                                            <Divider
                                                sx={{
                                                    my: 1,
                                                }}
                                            />

                                            <TextField
                                                size="small"
                                                fullWidth
                                                label="Buscar servicio"
                                                value={
                                                    serviceSearch
                                                }
                                                disabled={
                                                    submitting
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setServiceSearch(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                sx={{
                                                    mb: 2,
                                                }}
                                            />

                                            {loadingServices ? (
                                                <Box
                                                    sx={{
                                                        py: 3,
                                                        display:
                                                            "flex",
                                                        justifyContent:
                                                            "center",
                                                    }}
                                                >
                                                    <CircularProgress
                                                        size={
                                                            28
                                                        }
                                                    />
                                                </Box>
                                            ) : filteredAvailableServices.length ===
                                                0 ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {serviceSearch.trim()
                                                        ? "No se encontraron servicios"
                                                        : "No hay servicios disponibles"}
                                                </Typography>
                                            ) : (
                                                <Stack spacing={1}>
                                                    {filteredAvailableServices.map(
                                                        (
                                                            service
                                                        ) => {
                                                            const serviceId =
                                                                getSerProId(
                                                                    service
                                                                );

                                                            return (
                                                                <Paper
                                                                    key={
                                                                        serviceId
                                                                    }
                                                                    variant="outlined"
                                                                    sx={{
                                                                        p: 1.5,
                                                                        display:
                                                                            "flex",
                                                                        justifyContent:
                                                                            "space-between",
                                                                        alignItems:
                                                                            "center",
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography
                                                                            variant="body2"
                                                                            fontWeight={
                                                                                600
                                                                            }
                                                                        >
                                                                            {getSerProNom(
                                                                                service
                                                                            )}
                                                                        </Typography>

                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                        >
                                                                            {getProNom(
                                                                                service
                                                                            )}{" "}
                                                                            —{" "}
                                                                            {getTipSerNom(
                                                                                service
                                                                            )}
                                                                        </Typography>
                                                                    </Box>

                                                                    <IconButton
                                                                        type="button"
                                                                        color="primary"
                                                                        size="small"
                                                                        disabled={
                                                                            submitting
                                                                        }
                                                                        onClick={() =>
                                                                            handleAddService(
                                                                                service
                                                                            )
                                                                        }
                                                                        aria-label="Agregar servicio"
                                                                    >
                                                                        <AddCircleIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Paper>
                                                            );
                                                        }
                                                    )}
                                                </Stack>
                                            )}
                                        </Paper>
                                    </Grid>

                                    {/* ============================= */}
                                    {/* SERVICIOS ASIGNADOS */}
                                    {/* ============================= */}

                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: 2,
                                                height:
                                                    "100%",
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <RemoveCircleIcon color="primary" />

                                                Servicios asignados
                                            </Typography>

                                            <Divider
                                                sx={{
                                                    my: 1,
                                                }}
                                            />

                                            {assignedServices.length ===
                                                0 ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    No hay servicios asignados
                                                </Typography>
                                            ) : (
                                                <Stack spacing={1}>
                                                    {assignedServices.map(
                                                        (
                                                            service
                                                        ) => {
                                                            const serviceId =
                                                                getSerProId(
                                                                    service
                                                                );

                                                            const assignment =
                                                                assignedServicesData.find(
                                                                    (
                                                                        item
                                                                    ) =>
                                                                        item.serProId ===
                                                                        serviceId
                                                                );

                                                            return (
                                                                <Paper
                                                                    key={
                                                                        serviceId
                                                                    }
                                                                    variant="outlined"
                                                                    sx={{
                                                                        p: 1.5,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            justifyContent:
                                                                                "space-between",
                                                                            alignItems:
                                                                                "flex-start",
                                                                            gap: 1,
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                flex: 1,
                                                                                minWidth: 0,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                fontWeight={
                                                                                    600
                                                                                }
                                                                            >
                                                                                {getSerProNom(
                                                                                    service
                                                                                )}
                                                                            </Typography>

                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                            >
                                                                                {getProNom(
                                                                                    service
                                                                                )}{" "}
                                                                                —{" "}
                                                                                {getTipSerNom(
                                                                                    service
                                                                                )}
                                                                            </Typography>

                                                                            <TextField
                                                                                size="small"
                                                                                fullWidth
                                                                                label="Función en el servicio"
                                                                                placeholder="Ej. CONDUCTOR"
                                                                                disabled={
                                                                                    submitting
                                                                                }
                                                                                value={
                                                                                    assignment?.rolSerPro ??
                                                                                    ""
                                                                                }
                                                                                onChange={(
                                                                                    event
                                                                                ) =>
                                                                                    handleServiceRoleChange(
                                                                                        serviceId,
                                                                                        event
                                                                                            .target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                                sx={{
                                                                                    mt: 1.5,
                                                                                }}
                                                                            />
                                                                        </Box>

                                                                        <IconButton
                                                                            type="button"
                                                                            color="error"
                                                                            size="small"
                                                                            disabled={
                                                                                submitting
                                                                            }
                                                                            onClick={() =>
                                                                                handleRemoveService(
                                                                                    serviceId
                                                                                )
                                                                            }
                                                                            aria-label="Quitar servicio"
                                                                        >
                                                                            <RemoveCircleIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Paper>
                                                            );
                                                        }
                                                    )}
                                                </Stack>
                                            )}

                                            {errors
                                                .serviciosProyecto && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                        sx={{
                                                            mt: 1,
                                                            display:
                                                                "block",
                                                        }}
                                                    >
                                                        {
                                                            errors
                                                                .serviciosProyecto
                                                                .message
                                                        }
                                                    </Typography>
                                                )}
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 2,
                        bgcolor:
                            "grey.100",
                    }}
                >
                    <Button
                        type="button"
                        onClick={
                            handleFormClose
                        }
                        variant="outlined"
                        color="inherit"
                        disabled={
                            submitting
                        }
                    >
                        Cancelar
                    </Button>

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={
                            submitting
                        }
                        startIcon={
                            submitting
                                ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                )
                                : null
                        }
                    >
                        {submitting
                            ? "Guardando..."
                            : isEditing
                                ? "Actualizar"
                                : "Guardar"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default EmployeeForm;